import type { CSSProperties } from 'react';

import { copy } from '@shared/copy';
import { Skeleton, SkeletonHead, SkeletonPanel } from '@shared/ui/Skeleton';

/**
 * The CONSOLE-WIDE floor — the hydrate fallback the guarded branch shows before
 * the session read has answered.
 *
 * It stands in for the WHOLE SHELL, not for a page. React Router mounts a
 * `HydrateFallback` INSTEAD of the layout route's element, so a floor that drew
 * page blocks alone painted a bare column with no top bar, no navigation and no
 * gutter — the console's first paint on every cold load — and the shell then
 * dropped in over it. The bar, the navigation column and the footer band below
 * are the shell's own boxes on the shell's own `max-w-container` grid, so the
 * swap moves nothing.
 *
 * The real `Topbar` and `SideNav` are NOT mounted here, and that is deliberate:
 * the navigation is filtered by the capabilities the session read is still
 * fetching, so a real column would either draw every entry and then remove
 * some, or draw none. A column of placeholder rows states «navigation stands
 * here» and claims nothing about which entries this operator holds.
 *
 * One **polite** announcement per surface, with the shapes themselves
 * `aria-hidden`: a skeleton announced shape by shape is a screen reader reading
 * furniture.
 *
 * The sweep stops dead under `prefers-reduced-motion`, which the global
 * stylesheet enforces for every animation at once.
 */
export function PendingRoute() {
  return (
    <div className="flex min-h-dvh flex-col" style={{ '--sidenav-inline-size': '232px' } as CSSProperties}>
      <p role="status" className="sr-only">
        {copy.state.loading}
      </p>

      {/* The top bar, at its own `px-gutter py-3` rhythm. */}
      <div
        aria-hidden="true"
        className="flex items-center gap-1 border-b border-transparent px-gutter py-3 sm:gap-3"
      >
        <Skeleton className="h-6 w-6 shrink-0 rounded-sm" />
        <Skeleton className="h-4 w-[5rem]" />
        <span className="ms-auto" />
        <Skeleton className="h-[2.75rem] w-[2.75rem] rounded-sm" />
        <Skeleton className="h-[2.75rem] w-[2.75rem] rounded-sm" />
        <Skeleton className="h-[2.25rem] w-[2.25rem] rounded-pill" />
      </div>

      <div className="mx-auto w-full max-w-container flex-1 px-gutter lg:grid lg:items-start lg:gap-8 lg:[grid-template-columns:var(--sidenav-inline-size)_minmax(0,1fr)] xl:gap-9">
        {/* The navigation column exists from the desktop tier only — below it
            the real column is a sheet behind a toggle, and a placeholder for a
            closed sheet would be a placeholder for nothing. */}
        <div aria-hidden="true" className="hidden py-4 pe-2 lg:grid lg:gap-2">
          {Array.from({ length: 9 }, (_, index) => (
            <Skeleton key={index} className={index % 4 === 0 ? 'mt-4 h-3 w-[4rem]' : 'h-9 w-full'} />
          ))}
        </div>

        <div className="min-w-0 self-stretch pb-8">
          <SkeletonHead />
          <SkeletonPanel className="h-[10rem]" />
        </div>
      </div>

      <div aria-hidden="true" className="mt-section-gap border-t border-border bg-bg-sunken pb-8 pt-7">
        <div className="mx-auto w-full max-w-container px-gutter">
          <Skeleton className="h-4 w-[10rem]" />
        </div>
      </div>
    </div>
  );
}
